import React from 'react';
import type { ValidatedRow, RowStatus } from '../../../../../hooks/useImportChartOfAccounts';

interface ReviewStepProps { rows: ValidatedRow[]; }

export default function ReviewStep({ rows }: ReviewStepProps) {
  const ready = rows.filter((r) => r.status === 'ready').length;
  const dup = rows.filter((r) => r.status === 'duplicate').length;
  const err = rows.filter((r) => r.status === 'error').length;
  const sample = rows.slice(0, 8);
  const remaining = rows.length - sample.length;

  return (
    <div>
      <div className="flex gap-6 pb-3 border-b border-lineSoft text-[13px]">
        <div className="inline-flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: '#0F6E56' }}></span>
          <span className="font-bold text-ink text-[15px]">{ready}</span>
          <span className="text-ink2">ready to import</span>
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: '#D4A017' }}></span>
          <span className="font-bold text-ink text-[15px]">{dup}</span>
          <span className="text-ink2">duplicate</span>
        </div>
        <div className="inline-flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: '#8B1538' }}></span>
          <span className="font-bold text-ink text-[15px]">{err}</span>
          <span className="text-ink2">errors</span>
        </div>
      </div>

      <div className="border border-line rounded-lg overflow-hidden mt-4">
        <div className="grid gap-3 px-3.5 py-2.5 bg-panel border-b border-line text-[11px] font-bold text-ink3 uppercase tracking-wider" style={{ gridTemplateColumns: '70px 1.3fr 1fr 1fr 110px' }}>
          <div>Row</div><div>Account name</div><div>Type</div><div>Detail type</div><div className="text-right">Status</div>
        </div>
        {sample.map((row) => {
          const leftBorder = row.status === 'error' ? '3px solid #8B1538' : row.status === 'duplicate' ? '3px solid #D4A017' : 'none';
          return (
            <div
              key={row.rowNum}
              className="grid gap-3 py-2.5 border-b border-lineSoft last:border-b-0 items-center text-[12.5px]"
              style={{ gridTemplateColumns: '70px 1.3fr 1fr 1fr 110px', paddingLeft: row.status !== 'ready' ? 11 : 14, paddingRight: 14, borderLeft: leftBorder }}
            >
              <div className="text-ink4 text-[11.5px]" style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{row.rowNum}</div>
              <div>
                <div className="font-semibold" style={{ color: row.name ? '#0E1A1A' : '#96A0AB' }}>{row.name || '(empty)'}</div>
                {row.errorMessage && (
                  <div className="text-[11px] mt-0.5 font-medium" style={{ color: row.status === 'duplicate' ? '#8A6A00' : '#8B1538' }}>{row.errorMessage}</div>
                )}
              </div>
              <div style={{ color: row.status === 'error' && row.accountType ? '#8B1538' : '#0E1A1A' }}>{row.accountType || '-'}</div>
              <div>{row.detailType || '-'}</div>
              <div className="text-right"><StatusChip status={row.status} /></div>
            </div>
          );
        })}
        {remaining > 0 && (
          <div className="px-3.5 py-2.5 border-t border-lineSoft text-[12.5px] text-ink4">... {remaining} more rows</div>
        )}
      </div>
    </div>
  );
}

function StatusChip({ status }: { status: RowStatus }) {
  const map: Record<RowStatus, { color: string; label: string }> = {
    ready: { color: '#0F6E56', label: 'Ready' },
    duplicate: { color: '#8A6A00', label: 'Duplicate' },
    error: { color: '#8B1538', label: 'Error' },
  };
  const { color, label } = map[status];
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold" style={{ color }}>
      <span className="w-[7px] h-[7px] rounded-full" style={{ background: color }}></span>
      {label}
    </span>
  );
}